import React, { useState } from "react";
import * as Yup from "yup";
import {
  Form,
  Row,
  Col,
  TextBox,
  Button,
  Checkbox,
  Modal,
  ValidationSummary,
  Select,
} from "@countysuite/ui";
import {
  LackOfProsecutionTemplateDto,
  DefaultLOPTemplateResponse,
  DispositionCategoryType,
} from "../../LackOfProsecutionTypes";
import { Http } from "utils/Http";
import { HelpIconButton } from "components/HelpIconButton/HelpIconButton";
import { DropdownCategory } from "types/CivilTypes";

export interface Props {
  show: boolean;
  onHide: () => void;
  onTemplateAdded: (newTemplateId: number) => void;
  signatures: DropdownCategory[];
  dispositionCategories: DispositionCategoryType[];
}

export const CreateNewTemplateModal: React.FC<Props> = (props: Props) => {
  const [name, setName] = useState("");
  const [sequence, setSequence] = useState(0);
  const [defaultDaysOfInactivity, setDefaultDaysOfInactivity] = useState(365);
  const [visible, setVisible] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [signatureID, setDefaultSignatureId] = useState(0);
  const [excludeZeroDollarJudgments, setExcludeZeroDollarJudgement] = useState(
    false
  );
  const [dispositionCategoryType, setDispositionCategoryType] = useState(0);
  const [hideDeadlineDate, setHideDeadlineDate] = useState(false);
  const [referenceDateLabel, setReferenceDateLabel] = useState("");
  const [showParticipantListButton, setShowParticipantListButton] = useState(
    false
  );
  const [showLettersSelect, setShowLettersSelect] = useState(false);
  const [showPretrialDatePicker, setShowPretrialDatePicker] = useState(false);
  const [ignoreJudgmentCases, setIgnoreJudgementCases] = useState(false);

  function resetAllState() {
    setName("");
    setSequence(0);
    setDefaultDaysOfInactivity(365);
    setVisible(false);
    setServerErrors([]);
    setDefaultSignatureId(0);
    setExcludeZeroDollarJudgement(false);
    setDispositionCategoryType(0);
    setHideDeadlineDate(false);
    setReferenceDateLabel("");
    setShowParticipantListButton(false);
    setShowLettersSelect(false);
    setIgnoreJudgementCases(false);
  }

  function handleHide() {
    resetAllState();
    props.onHide();
  }

  async function postTemplate() {
    const dct = props.dispositionCategories.find(
      (dct) => dct.id === dispositionCategoryType
    );
    const newTemplate: LackOfProsecutionTemplateDto = {
      id: 0,
      defaultDaysOfInactivity,
      name,
      sequence,
      visible,
      defaultSignatureOfficialID: signatureID,
      excludeZeroDollarJudgments: excludeZeroDollarJudgments,
      dispositionTypeID: dct?.dispositionTypeID ?? 0,
      dispositionCategoryID: dct?.dispositionCategoryID ?? 0,
      hideDeadlineDate: hideDeadlineDate,
      referenceDateLabel: referenceDateLabel,
      showParticipantListButton: showParticipantListButton,
      showLettersSelect: showLettersSelect,
      lettersIDs: [],
      showPretrialDatePicker: showPretrialDatePicker,
      ignoreJudgementCases: ignoreJudgmentCases,
    };
    const response = await Http.post<DefaultLOPTemplateResponse>({
      url: "LackOfProsecutionTemplate/SaveDetails",
      data: newTemplate,
    });
    if (response.success && response.templateID) {
      resetAllState();
      props.onTemplateAdded(response.templateID);
    } else {
      setServerErrors(
        response.errors || ["There was a problem saving your template."]
      );
    }
  }

  const addTemplateValidation = Yup.object().shape({
    name: Yup.string().required("Please enter a name for this template."),
    defaultDaysOfInactivity: Yup.number()
      .transform((cv) => (isNaN(cv) ? undefined : cv))
      .required("Please enter a number for days of inactivity.")
      .min(1, "The days of inactivity must be at least 1."),
    sequence: Yup.number()
      .transform((cv) => (isNaN(cv) ? undefined : cv))
      .required(
        "Please enter the position relative to other templates, a value between 1 and 9999."
      )
      .min(1, "The position in dropdowns must be at least 1")
      .max(9999, "The position in dropdowns cannot exceed 9999."),
    dispositionCategoryType: Yup.number().min(1, "Please select a disposition type")
  });

  //Add the select disposition type to the list
  var dispositions = [{
    dispositionCategoryID: 0,
    dispositionTypeID: 0,
    dispositionCategoryName: 'Please select a disposition type',
    dispositionTypeName: '',
    name: 'Please select a type',
    id: 0
  }];
  dispositions.push(...props.dispositionCategories);

  return (
    <Form
      onSubmit={postTemplate}
      showErrors={false}
      validationSchema={addTemplateValidation}
      values={{ name, defaultDaysOfInactivity, sequence, dispositionCategoryType }}
    >
      {(formProps) => (
        <Modal
          title="Add New Template"
          onHide={handleHide}
          show={props.show}
          buttons={
            <>
              <HelpIconButton style={{ marginRight: "6px" }}>
                <p>
                  Please choose a name for your template, the sequence number in
                  dropdown menus, and if it's currently visible in the system.
                </p>
                <p>
                  For Days of Inactivity, this is how many days that the
                  template will subtract from today's date when trying to
                  determine the Date of Inactivity for the case search.
                </p>
              </HelpIconButton>
              <Button type="button" onClick={handleHide}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={formProps.handleSubmit}
                color="success"
              >
                Add New Template
              </Button>
            </>
          }
        >
          <Row>
            <Col xs={12}>
              <ValidationSummary
                errors={
                  serverErrors.length
                    ? serverErrors
                    : formProps.errorSummary.errors
                }
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <TextBox
                name="name"
                label="Template Name"
                onChange={(e: any) => setName(e.target.value)}
                value={name}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <TextBox
                name="sequence"
                label="Sequence Number In Dropdown"
                onChange={(e: any) => setSequence(e.target.value)}
                value={sequence}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <TextBox
                name="defaultDaysOfInactivity"
                label="Days of Inactivity"
                onChange={(e: any) =>
                  setDefaultDaysOfInactivity(e.target.value)
                }
                value={defaultDaysOfInactivity}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Select
                label="Choose Disposition Type to use when Terminating Cases"
                onChange={(e: any) =>
                  setDispositionCategoryType(Number(e.target.value))
                }
                name="dispositionCategoryTypeID"
                value={dispositionCategoryType}
                options={dispositions}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={3}>
              <Checkbox
                name="visible"
                label="Visible In Dropdowns"
                onChange={(e: any) => setVisible(!!e.target.checked)}
                checked={visible}
              >
                Visible in Dropdowns?
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="excludeZeroDollarJudgments"
                checked={excludeZeroDollarJudgments}
                onChange={(e: any) =>
                  setExcludeZeroDollarJudgement(!!e.target.checked)
                }
              >
                Exclude cases with $0.00 judgments
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="ignoreJudgmentCases"
                checked={ignoreJudgmentCases}
                onChange={(e: any) =>
                  setIgnoreJudgementCases(!!e.target.checked)
                }
              >
                Exclude cases that have a judgment
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="showPretrialDatePicker"
                checked={showPretrialDatePicker}
                onChange={(e: any) =>
                  setShowPretrialDatePicker(!!e.target.checked)
                }
              >
                Show Pretial Date Picker On Defintion
              </Checkbox>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={3}>
              <Checkbox
                name="hideDeadlineDate"
                checked={hideDeadlineDate}
                onChange={(e: any) => setHideDeadlineDate(!!e.target.checked)}
              >
                Hide Deadline Date
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="showParticipantListButton"
                checked={showParticipantListButton}
                onChange={(e: any) =>
                  setShowParticipantListButton(!!e.target.checked)
                }
              >
                Show Participant List Button
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="showLettersSelect"
                checked={showLettersSelect}
                onChange={(e: any) => setShowLettersSelect(!!e.target.checked)}
              >
                Add Checkbox Column To Letters Step
              </Checkbox>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Select
                name="signatureId"
                label="Default Signature"
                onChange={(e: any) => setDefaultSignatureId(e.target.value)}
                options={props.signatures}
              />
            </Col>
          </Row>
        </Modal>
      )}
    </Form>
  );
};
